import { WIDGET_HOME_LIMITS, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Field, Input, Select, Skeleton } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { HomeErrorCode, HomeOptionForm } from "./home-model";
import type { HomeChoices } from "./use-home-data";

const K = "deployKnowledge.widgetStudio.home";

export const TARGET_FIELD_ID = "home-option-target";

type Errors = Partial<Record<keyof HomeOptionForm, HomeErrorCode>>;

interface TargetProps {
  option: HomeOptionForm;
  errors: Errors;
  workflows: HomeChoices;
  bookings: HomeChoices;
  agentId: string;
  onChange: (patch: Partial<HomeOptionForm>) => void;
}

interface PickerProps {
  label: string;
  placeholder: string;
  hint: string;
  none: string;
  loadError: string;
  error: string | undefined;
  value: string;
  choices: HomeChoices;
  createHref: string;
  createLabel: string;
  onPick: (id: string) => void;
}

function ChoicePicker({ label, placeholder, hint, none, loadError, error, value, choices, createHref, createLabel, onPick }: PickerProps) {
  const { t } = useTranslation();
  if (choices.loading) return <Skeleton className="h-10 w-full" data-testid="home-target-loading" />;
  if (choices.failed) return <p className="text-xs text-fg-subtle">{loadError}</p>;
  const stale = value !== "" && !choices.choices.some((choice) => choice.id === value);
  return (
    <Field
      id={TARGET_FIELD_ID}
      label={label}
      error={error}
      hint={
        choices.choices.length > 0 ? (
          hint
        ) : (
          <>
            {none}{" "}
            <AppLink href={createHref} target="_blank" rel="noreferrer" className="font-semibold text-primary-ink hover:underline">
              {createLabel}
            </AppLink>
          </>
        )
      }
    >
      <Select value={value} onChange={(event) => { onPick(event.target.value); }}>
        <option value="">{placeholder}</option>
        {stale ? (
          <option value={value} disabled>
            {choices.names.get(value) ?? t(`${K}.dialog.unavailable`)}
          </option>
        ) : null}
        {choices.choices.map((choice) => (
          <option key={choice.id} value={choice.id}>
            {choice.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** The one field the chosen action needs (a person needs none). */
export function HomeOptionTarget({ option, errors, workflows, bookings, agentId, onChange }: TargetProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const message = (code: HomeErrorCode | undefined) => (code ? t(`${K}.errors.${code}`) : undefined);
  if (option.action === "workflow") {
    return (
      <ChoicePicker
        label={t(`${K}.dialog.workflow`)} placeholder={t(`${K}.dialog.workflowPick`)} hint={t(`${K}.dialog.workflowHint`)} none={t(`${K}.dialog.workflowNone`)}
        loadError={t(`${K}.dialog.workflowLoadError`)} error={message(errors.workflow_id)} value={option.workflow_id} choices={workflows}
        createHref={consoleRoutes.workflows(summary.slug)} createLabel={t(`${K}.dialog.workflowCreate`)} onPick={(id) => { onChange({ workflow_id: id }); }}
      />
    );
  }
  if (option.action === "booking") {
    return (
      <ChoicePicker
        label={t(`${K}.dialog.booking`)} placeholder={t(`${K}.dialog.bookingPick`)} hint={t(`${K}.dialog.bookingHint`)} none={t(`${K}.dialog.bookingNone`)}
        loadError={t(`${K}.dialog.bookingLoadError`)} error={message(errors.action_id)} value={option.action_id} choices={bookings}
        createHref={consoleRoutes.actions(summary.slug, agentId)} createLabel={t(`${K}.dialog.bookingCreate`)} onPick={(id) => { onChange({ action_id: id }); }}
      />
    );
  }
  if (option.action === "question") {
    return (
      <Field id={TARGET_FIELD_ID} label={t(`${K}.dialog.question`)} hint={t(`${K}.dialog.questionHint`)} error={message(errors.question)}>
        <Input maxLength={WIDGET_HOME_LIMITS.question} value={option.question} onChange={(event) => { onChange({ question: event.target.value }); }} />
      </Field>
    );
  }
  if (option.action === "link") {
    return (
      <Field id={TARGET_FIELD_ID} label={t(`${K}.dialog.link`)} hint={t(`${K}.dialog.linkHint`)} error={message(errors.url)}>
        <Input
          className="font-mono text-xs" type="url" inputMode="url" placeholder="https://" maxLength={WIDGET_HOME_LIMITS.url}
          value={option.url} onChange={(event) => { onChange({ url: event.target.value }); }}
        />
      </Field>
    );
  }
  return <p className="text-xs text-fg-subtle">{t(`${K}.dialog.personHint`)}</p>;
}
